import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { isOwnWrite } from './ownWrites';
import {
  applyRemoteGhost,
  broadcastEquipmentMove,
  getCanvasCollabMode,
  setEquipMoveSender,
  shouldIgnoreRemoteDesign,
} from './canvasCollab';
import { subscribeEquipmentDrag } from '../../utils/canvasDrag';
import { useCanvasCollabStore } from '../../store/canvasCollabStore';

type ChangePayload = RealtimePostgresChangesPayload<Record<string, unknown>>;

let channel: RealtimeChannel | null = null;
let activeCollectionId: string | null = null;
let activeUserId: string | null = null;
let unsubDrag: (() => void) | null = null;

function rowOf(payload: ChangePayload): Record<string, unknown> | null {
  const row = (payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old) as
    | Record<string, unknown>
    | null;
  return row && typeof row === 'object' ? row : null;
}

async function handleChange(table: string, payload: ChangePayload): Promise<void> {
  const event = payload.eventType;
  const row = rowOf(payload);
  if (!row && event !== 'DELETE') return;
  const id = (row?.id as string | undefined) ?? (payload.old as { id?: string } | null)?.id;

  if (id && isOwnWrite(table, id)) return;

  try {
    if (table === 'sites' || table === 'site_equipment' || table === 'jobs' || table === 'job_recurrence_rules') {
      const { useSitesStore } = await import('../../store/sitesStore');
      useSitesStore.getState().applyRemoteChange(table, event, row, id);
      return;
    }
    if (table === 'stock_requests' || table === 'stock_request_lines') {
      const { useStockRequestStore } = await import('../../store/stockRequestStore');
      useStockRequestStore.getState().applyRemoteRow(
        table,
        event,
        row ?? { id }
      );
      return;
    }
    if (table === 'stock_request_notifications') {
      const { useStockNotificationsStore } = await import('../../store/stockNotificationsStore');
      useStockNotificationsStore.getState().applyRemoteRow(event, row ?? { id });
      return;
    }
    if (table === 'chat_messages') {
      const { useChatStore } = await import('../../store/chatStore');
      useChatStore.getState().applyRemoteRow(event, row ?? { id });
      return;
    }
    if (table === 'designs') {
      if (getCanvasCollabMode() === 'default') return;
      const designRow = row ?? { id };
      if (event !== 'DELETE' && shouldIgnoreRemoteDesign({
        client_write_id: designRow.client_write_id as string | null | undefined,
        updated_at: designRow.updated_at as string | undefined,
      })) {
        return;
      }
      const { useProjectStore } = await import('../../store/projectStore');
      useProjectStore.getState().applyRemoteDesign(event, designRow);
    }
  } catch (err) {
    console.warn('[realtime] handler failed', table, err);
  }
}

function attachTable(ch: RealtimeChannel, table: string, filter?: string): void {
  const cfg: { event: '*'; schema: 'public'; table: string; filter?: string } = {
    event: '*',
    schema: 'public',
    table,
  };
  if (filter) cfg.filter = filter;
  ch.on('postgres_changes', cfg, (payload) => {
    void handleChange(table, payload as ChangePayload);
  });
}

export function stopCollectionRealtime(): void {
  unsubDrag?.();
  unsubDrag = null;
  setEquipMoveSender(null);
  useCanvasCollabStore.getState().clearGhosts();
  if (channel) {
    try {
      void supabase.removeChannel(channel);
    } catch (err) {
      console.warn('[realtime] unsubscribe failed', err);
    }
  }
  channel = null;
}

export function startCollectionRealtime(collectionId: string, userId: string): void {
  stopCollectionRealtime();
  activeCollectionId = collectionId;
  activeUserId = userId;

  try {
    const ch = supabase.channel(`collection:${collectionId}`);
    attachTable(ch, 'sites', `collection_id=eq.${collectionId}`);
    attachTable(ch, 'jobs', `collection_id=eq.${collectionId}`);
    attachTable(ch, 'stock_requests', `collection_id=eq.${collectionId}`);
    attachTable(ch, 'stock_request_notifications', `recipient_id=eq.${userId}`);
    attachTable(ch, 'site_equipment');
    attachTable(ch, 'job_recurrence_rules');
    attachTable(ch, 'stock_request_lines');
    attachTable(ch, 'chat_messages');

    const mode = getCanvasCollabMode();
    if (mode !== 'default') {
      attachTable(ch, 'designs', `collection_id=eq.${collectionId}`);
      ch.on('broadcast', { event: 'equip-move' }, ({ payload }) => {
        const data = payload as { id?: string; position?: { x: number; y: number } | null; senderId?: string };
        if (!data?.id || data.senderId === userId) return;
        applyRemoteGhost(data.id, data.position ?? null);
      });
      setEquipMoveSender((equipmentId, position) => {
        void ch.send({
          type: 'broadcast',
          event: 'equip-move',
          payload: { id: equipmentId, position, senderId: userId },
        });
      });
      if (mode === 'livePreview') {
        unsubDrag = subscribeEquipmentDrag((equipmentId, position) => {
          broadcastEquipmentMove(equipmentId, position);
        });
      }
    }

    ch.subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        console.warn('[realtime] channel', status);
      }
    });
    channel = ch;
  } catch (err) {
    console.warn('[realtime] subscribe failed; falling back to fetch-on-switch', err);
    channel = null;
  }
}

export function restartCollectionRealtime(): void {
  if (!activeCollectionId || !activeUserId) return;
  startCollectionRealtime(activeCollectionId, activeUserId);
}
